import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Users, MessageSquare, Coins, TrendingUp, BarChart3, FileText, Bot, Zap, Flame } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import ViralScoreRadar from '../components/signature/ViralScoreRadar';
import UsageLogsTable from '../components/UsageLogsTable';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, value, sub, color = 'primary' }) => {
  const colors = {
    primary: 'bg-primary-50 text-primary-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    blue: 'bg-blue-50 text-blue-600',
  };
  return (
    <Card className="p-5 hover-glow transition-all">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-neutral-500 font-medium mb-1">{label}</p>
          <p className="text-2xl font-bold text-neutral-900 tabular-nums">{value}</p>
          {sub && <p className="text-xs text-neutral-400 mt-1">{sub}</p>}
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-5 h-5" strokeWidth={1.5} />
        </div>
      </div>
    </Card>
  );
};

const CHART_COLORS = ['#FF5436', '#FF6B50', '#60A5FA', '#A78BFA', '#F59E0B', '#34D399', '#F472B6', '#FB923C', '#818CF8', '#2DD4BF'];

const AdminStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topViral, setTopViral] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [sRes, vRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/stats`),
          axios.get(`${API_URL}/api/user-videos/admin/top-viral?limit=5`).catch(() => ({ data: [] })),
        ]);
        setStats(sRes.data);
        setTopViral(vRes.data || []);
      } catch { toast.error('獲取統計失敗'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading || !stats) {
    return (
      <>
        <header className="bg-white border-b border-neutral-200 px-4 md:px-8 py-4 md:py-6 flex-shrink-0">
          <h1 className="text-lg md:text-2xl font-heading font-bold text-neutral-900">數據總覽</h1>
        </header>
        <div className="flex-1 flex items-center justify-center"><p className="text-neutral-400 text-sm">載入中...</p></div>
      </>
    );
  }

  const o = stats.overview;
  const formatNum = (n) => {
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toString();
  };

  return (
    <>
      <header className="bg-white border-b border-neutral-200 px-4 md:px-8 py-4 md:py-6 flex-shrink-0">
        <h1 className="text-lg md:text-2xl font-heading font-bold text-neutral-900">數據總覽</h1>
        <p className="text-xs md:text-sm text-neutral-500 mt-0.5">全面了解平台運營情況</p>
      </header>

      <main className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className="max-w-7xl mx-auto space-y-6">

          {/* ── Overview Cards ── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <StatCard icon={Users} label="總用戶數" value={o.total_users} sub={`近 7 天活躍：${o.active_users_7d}`} />
            <StatCard icon={MessageSquare} label="總對答數" value={formatNum(o.total_exchanges)} sub="每次 AI 回覆計 1 次" color="blue" />
            <StatCard icon={FileText} label="總生成文字" value={formatNum(o.total_generated_chars) + ' 字'} sub={`共 ${formatNum(o.total_tokens)} tokens`} color="green" />
            <StatCard icon={Coins} label="Credits 消耗" value={o.total_credits_spent.toFixed(0)} sub={`充值收入：${o.total_revenue_credits.toFixed(0)}`} color="amber" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <StatCard icon={Bot} label="啟用 Agent" value={o.total_agents} />
            <StatCard icon={TrendingUp} label="利潤率" value={`${o.profit_margin}%`} sub={`利潤 $${o.profit.toFixed(2)} USD`} color="green" />
            <StatCard icon={Zap} label="API 成本" value={`$${o.total_cost.toFixed(2)}`} sub="USD" color="amber" />
            <StatCard icon={BarChart3} label="利潤倍數" value={`${o.profit_multiplier}x`} color="blue" />
          </div>

          {/* ── Charts Row ── */}
          <div className="grid md:grid-cols-2 gap-4 md:gap-6">
            {/* Daily Conversations + Active Users */}
            <Card className="p-5">
              <h3 className="font-semibold text-sm text-neutral-900 mb-4">每日對答數 & 活躍用戶（近 30 天）</h3>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={stats.daily_trend}>
                  <CartesianGrid strokeDasharray="2 4" stroke="#222428" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#7A7E86' }} axisLine={{ stroke: '#222428' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#7A7E86' }} axisLine={{ stroke: '#222428' }} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #222428', fontSize: 12, background: '#0E1014', color: '#FFFFFF' }} cursor={{ stroke: 'rgba(255,84,54,0.2)' }} />
                  <Area type="monotone" dataKey="exchanges" name="對答" stroke="#FF5436" fill="#FF5436" fillOpacity={0.18} strokeWidth={2} />
                  <Area type="monotone" dataKey="active_users" name="活躍用戶" stroke="#60A5FA" fill="#60A5FA" fillOpacity={0.12} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </Card>

            {/* Daily Credits */}
            <Card className="p-5">
              <h3 className="font-semibold text-sm text-neutral-900 mb-4">每日 Credits 消耗（近 30 天）</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={stats.daily_trend}>
                  <CartesianGrid strokeDasharray="2 4" stroke="#222428" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#7A7E86' }} axisLine={{ stroke: '#222428' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#7A7E86' }} axisLine={{ stroke: '#222428' }} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #222428', fontSize: 12, background: '#0E1014', color: '#FFFFFF' }} cursor={{ fill: 'rgba(255,84,54,0.08)' }} />
                  <Bar dataKey="credits" name="Credits" fill="#FF5436" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {/* ── Agent Ranking + Top Users ── */}
          <div className="grid md:grid-cols-2 gap-4 md:gap-6">
            {/* Agent Usage Pie + List */}
            <Card className="p-5">
              <h3 className="font-semibold text-sm text-neutral-900 mb-4">Agent 使用排名</h3>
              <div className="flex gap-4">
                <div className="w-36 h-36 flex-shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={stats.agent_ranking} dataKey="conversations" nameKey="name" cx="50%" cy="50%" outerRadius={60} innerRadius={30}>
                        {stats.agent_ranking.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #222428', fontSize: 12, background: '#0E1014', color: '#FFFFFF' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-2 overflow-y-auto max-h-40">
                  {stats.agent_ranking.map((a, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                        <span className="text-base mr-1">{a.icon}</span>
                        <span className="text-neutral-700 truncate text-xs">{a.name}</span>
                      </div>
                      <span className="text-neutral-900 font-semibold text-xs tabular-nums ml-2">{a.exchanges}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Card>

            {/* Top Users */}
            <Card className="p-5">
              <h3 className="font-semibold text-sm text-neutral-900 mb-4">用戶活躍度排名 Top 10</h3>
              <div className="space-y-2 max-h-52 overflow-y-auto">
                {stats.top_users.map((u, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-neutral-100 last:border-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-5 h-5 bg-neutral-100 rounded-full flex items-center justify-center text-[10px] font-bold text-neutral-500 flex-shrink-0">{i + 1}</span>
                      <div className="min-w-0">
                        <div className="text-xs font-medium text-neutral-800 truncate">{u.name || u.email}</div>
                        <div className="text-[10px] text-neutral-400 truncate">{u.email}</div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2">
                      <div className="text-xs font-semibold text-neutral-800 tabular-nums">{u.exchanges} 對答</div>
                      <div className="text-[10px] text-neutral-400 tabular-nums">{u.credits_used} credits</div>
                    </div>
                  </div>
                ))}
                {stats.top_users.length === 0 && <p className="text-xs text-neutral-400 text-center py-4">暫無數據</p>}
              </div>
            </Card>
          </div>

          {/* ── Recent Logs (paginated) ── */}
          <UsageLogsTable />

          {/* ✨ Top 5 Viral Videos Ranking — signature radar showcase */}
          {topViral.length > 0 && (
            <Card className="p-5 mt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Flame className="w-4 h-4 text-lime-500" strokeWidth={1.75} />
                  <h3 className="font-semibold text-white text-sm">爆款指數 Top {topViral.length}</h3>
                </div>
                <span className="label-micro text-obsidian-400">VIRAL · RANKING</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {topViral.map((v, i) => {
                  const rankColor = i === 0 ? '#FF5436' : i === 1 ? '#FF6B50' : i === 2 ? '#FBBF24' : '#7A7E86';
                  return (
                    <div key={v.id} className="relative">
                      <div
                        className="absolute -top-2 -left-2 w-7 h-7 rounded-full font-display font-bold text-sm flex items-center justify-center z-10 tabular-nums"
                        style={{
                          background: '#0E1014',
                          border: `2px solid ${rankColor}`,
                          color: rankColor,
                          boxShadow: `0 0 12px ${rankColor}55`,
                        }}
                      >
                        {i + 1}
                      </div>
                      <ViralScoreRadar analysis={v.ai_analysis} size={200} />
                      <div className="mt-2 px-1">
                        <div className="label-micro text-obsidian-400 truncate">{v.platform?.toUpperCase() || '—'}</div>
                        <div className="text-xs text-obsidian-100 truncate mt-0.5">
                          {v.ai_analysis?.topic || v.video_url}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </div>
      </main>
    </>
  );
};

export default AdminStats;
